import React, { useState, useRef, useEffect } from 'react';
import { MonitoringGoodsDetailDaily } from '@/types/api';
import { ChartContainer } from '@/components/atoms/Chart';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];
function formatDateWithDay(dateStr: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const weekday = WEEKDAY_KO[date.getDay()];
  return `${month}.${day}(${weekday})`;
}

const ChartHeader = ({
  keywords,
  selected,
  onClick,
}: {
  keywords: string[];
  selected: string;
  onClick: (keyword: string) => void;
}) => (
  <div className="flex flex-col gap-[8px]">
    <div className="flex w-fit flex-wrap gap-[6px] rounded-[8px] bg-gr-100 p-[6px]">
      {keywords.map((keyword) => (
        <button
          key={keyword}
          onClick={() => onClick(keyword)}
          className={`rounded-[8px]  px-[8px] py-[6px] transition-colors duration-300 Caption1M12 ${
            selected === keyword ? 'bg-white text-gr-700' : ' text-gr-700 '
          }`}
        >
          {keyword}
        </button>
      ))}
    </div>
  </div>
);

const GoodsDetailKeywordChart = ({ dailyList }: { dailyList: MonitoringGoodsDetailDaily[] }) => {
  const keywords = dailyList[0]?.RelatedKeywordsRanking?.map((k: any) => k.Keyword) || [];
  const [selected, setSelected] = useState<string>(keywords[0] || '');

  // 최근 showCount개만 사용 (최신순 정렬이 아니라면 reverse 필요)
  const sortedList = [...dailyList].sort(
    (a, b) => new Date(a.MonitoringDate).getTime() - new Date(b.MonitoringDate).getTime(),
  );
  const slicedList = sortedList.slice(-9);

  // 차트 데이터 생성
  const chartData = slicedList.map((day) => {
    const kw = day.RelatedKeywordsRanking.find((k: any) => k.Keyword === selected);
    return {
      date: day.MonitoringDate?.slice(0, 10) || '', // YYYY-MM-DD
      rank: kw ? (kw.Rank > 0 ? kw.Rank : 1000) : null,
    };
  });

  return (
    <div className="flex h-full w-full flex-col gap-[12px]">
      <ChartHeader keywords={keywords} selected={selected} onClick={setSelected} />
      <ChartContainer config={{}} className="h-[180px] min-h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 22, right: 10, bottom: 0, left: 0 }}>
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              interval={'preserveStartEnd'}
              tick={{
                fontSize: 12,
                fontWeight: 500,
                fill: '#53555E',
                textAnchor: 'middle',
              }}
              tickFormatter={formatDateWithDay}
            />
            <YAxis
              dataKey="rank"
              type="number"
              domain={[1, 1000]} // y축을 항상 1000~1로 고정
              reversed
              tickCount={10}
              allowDecimals={false}
              // allowDataOverflow={true} // ★ 이 부분 추가!
              tick={{
                fontSize: 10,
                fontWeight: 500,
                fill: '#53555E',
              }}
            />
            <Tooltip
              formatter={(value) => (value === 1000 ? '1000위 밖' : `${value}위`)}
              labelFormatter={(label) => `날짜: ${label}`}
              contentStyle={{ background: 'rgba(255,255,255,0.95)', borderRadius: 8 }}
            />
            <defs>
              <linearGradient id="fillMention" x1="0" y1="1" x2="0" y2="0">
                <stop offset="5%" stopColor="#04A5E21A" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#04A5E21A" stopOpacity={1} />
              </linearGradient>
            </defs>

            <Area
              dataKey="rank"
              type="monotone"
              fill="url(#fillMention)"
              fillOpacity={0.4}
              stroke="#4B93FF"
              strokeWidth={2}
              dot={{ stroke: '#4B93FF', strokeWidth: 2, r: 4, fill: '#fff' }}
              isAnimationActive={true}
              baseValue={1000} // 항상 1000(아래)에서 시작
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};

export default GoodsDetailKeywordChart;
